import React, { useEffect } from 'react';

import { useSnackbar } from 'notistack';

import RefreshIcon from '@mui/icons-material/Refresh';
import { Alert, Box, Button, Stack } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import PageSubheader from '~/components/Common/Elements/PageSubheader';

import { useHintRefreshQueue, useRemoveHintRefresh } from '~/hooks/apis/connection-google/useHintRefreshQueue';

import { HintCoverageAccountType, HintRefreshQueueEntry } from '~/services/ConnectionGoogleService';

interface Props {
  accountType: HintCoverageAccountType;
}

/**
 * Live view of the hint refresh queue (GR-4819). Entries are ephemeral: the next hint cycle
 * serves each property's cached itineraries and drops it from the queue, so an empty grid
 * minutes after enqueueing means the refresh went through — verify on the property's coverage
 * view, where every itinerary's "last hinted" jumps to just now.
 */
export default function HintRefreshQueuePanel({ accountType }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const { data: queue, isError, isFetching, refetch } = useHintRefreshQueue(accountType);
  const removeMutation = useRemoveHintRefresh(accountType);

  useEffect(() => {
    if (isError) {
      enqueueSnackbar('Failed to fetch the hint refresh queue', { variant: 'error' });
    }
  }, [isError, enqueueSnackbar]);

  const handleRemove = async (propertyId: string) => {
    try {
      const result = await removeMutation.mutateAsync([propertyId]);
      if (result.removed.length > 0) {
        enqueueSnackbar('Removed from the hint refresh queue', { variant: 'success' });
      } else {
        // Lost the race with a hint cycle: the property was already refreshed — same outcome.
        enqueueSnackbar('Already processed by a hint cycle', { variant: 'info' });
      }
    } catch (error) {
      enqueueSnackbar('Failed to remove from the hint refresh queue', { variant: 'error' });
      console.error('Failed to remove from the hint refresh queue', error);
    }
  };

  const columns: Array<GridColDef<HintRefreshQueueEntry>> = [
    { field: 'position', headerName: '#', width: 60 },
    { field: 'propertyId', headerName: 'Property ID', flex: 2, minWidth: 300 },
    { field: 'enqueuedBy', headerName: 'Enqueued By', flex: 1.5, minWidth: 220 },
    {
      field: 'enqueuedAt',
      headerName: 'Enqueued At',
      flex: 1,
      minWidth: 170,
      valueGetter: (value: string) => (value ? new Date(value).toLocaleString() : ''),
    },
    {
      field: 'eligibleItineraries',
      headerName: 'Itineraries to Refresh',
      flex: 1,
      minWidth: 160,
      type: 'number',
    },
    {
      field: 'actions',
      headerName: '',
      width: 110,
      sortable: false,
      renderCell: ({ row }) => (
        <Button
          size="small"
          color="error"
          disabled={removeMutation.isPending}
          onClick={() => handleRemove(row.propertyId)}
        >
          Remove
        </Button>
      ),
    },
  ];

  return (
    <Box mt={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <PageSubheader title="Hint Refresh Queue" />
        <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={() => refetch()}>
          Refresh
        </Button>
      </Stack>

      <Alert severity="info" sx={{ my: 2 }}>
        Properties queued for a hint refresh (from the &quot;Refresh GHA Prices&quot; bulk action on GHA Property
        Management). The next hint cycle re-hints every itinerary Google currently caches for them — prompting Google to
        re-pull fresh prices — then they leave this queue; no history is kept. Verify a finished refresh on the
        property&apos;s coverage view.
      </Alert>

      <Box height={400}>
        <DataGrid
          rows={queue ?? []}
          columns={columns}
          getRowId={(row) => row.propertyId}
          loading={isFetching}
          disableRowSelectionOnClick
          localeText={{ noRowsLabel: 'No properties queued' }}
        />
      </Box>
    </Box>
  );
}
